import type { Occurrence } from './occurrences.js';
import type { CheckinStatus } from './schemas.js';
import type { LocalDate } from './time.js';

export interface Progress {
  total: number;
  done: number;
  missed: number;
  skipped: number;
  open: number;
  /** done / total, or null when nothing on that day needs a check-in. */
  ratio: number | null;
}

export type StatusLookup = (key: string) => CheckinStatus | null | undefined;

export function computeProgress(
  occurrences: readonly Occurrence[],
  status: StatusLookup,
): Progress {
  const p: Progress = { total: 0, done: 0, missed: 0, skipped: 0, open: 0, ratio: null };
  for (const o of occurrences) {
    if (!o.requiresCheck) continue;
    p.total += 1;
    const s = status(o.key);
    if (s === 'done') p.done += 1;
    else if (s === 'missed') p.missed += 1;
    else if (s === 'skipped') p.skipped += 1;
    else p.open += 1;
  }
  p.ratio = p.total === 0 ? null : p.done / p.total;
  return p;
}

export type SuccessLevel = 'none' | 'low' | 'mid' | 'high';

/** Colour bucket for the week strip / month view. */
export function successLevel(ratio: number | null): SuccessLevel {
  if (ratio === null) return 'none';
  if (ratio >= 0.8) return 'high';
  if (ratio >= 0.5) return 'mid';
  return 'low';
}

export function groupByDate(occurrences: readonly Occurrence[]): Map<LocalDate, Occurrence[]> {
  const map = new Map<LocalDate, Occurrence[]>();
  for (const o of occurrences) {
    const list = map.get(o.date);
    if (list) list.push(o);
    else map.set(o.date, [o]);
  }
  return map;
}
